import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import { theme } from 'styles/theme'
import { breakpoints } from 'styles/mq'
import { useMedia } from 'hooks/useMedia'
import {
  Content,
  Country,
  Description,
  Map,
  ReadMore,
  Reverse,
  Item as StyledItem,
} from './styled'

type Props = {
  mapSrc: string
  countryName: string
  description: ReactNode
  onClose: () => void
  onReverse: () => void
  isPrevious: boolean
  isReversed?: boolean
  isSmall?: boolean
}

export const Inner: FC<
  Omit<Props, 'onClose' | 'isPrevious'> & {
    isBackside?: boolean
  }
> = ({
  mapSrc,
  countryName,
  description,
  onReverse,
  isBackside,
  isReversed,
  isSmall,
}) => (
  <Content
    layout
    animate={{ height: isReversed || !isSmall ? 'auto' : '10rem' }}
    data-expanded={isReversed ? 'true' : 'false'}
    data-reversed={isBackside ? 'true' : 'false'}
  >
    {!isBackside && <Map src={mapSrc} alt={countryName} />}
    <Country>{countryName}</Country>
    <Description>{description}</Description>
    <ReadMore onClick={onReverse} />
  </Content>
)

export const Item: FC<Props> = ({
  onClose,
  isReversed,
  isPrevious,
  ...props
}) => {
  const isSmall = useMedia([breakpoints.ltLarge], [true], false)
  const ref = useRef<HTMLLIElement>(null)
  const [boundingRect, setBoundingRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (isReversed && ref.current) {
      setBoundingRect(ref.current.getBoundingClientRect())
    } else if (!isReversed) {
      setBoundingRect(null)
    }
  }, [isReversed])

  return (
    <StyledItem
      ref={ref}
      layout
      animate={{
        ...(boundingRect
          ? {
              translateX: Math.round(
                window.innerWidth / 2 -
                  boundingRect.left -
                  boundingRect.width / 2
              ),
            }
          : {
              translateX: 0,
              translateY: 0,
            }),
        zIndex: isReversed ? 100 : isPrevious ? 50 : 0,
        transition: {
          type: 'tween',
          duration: 0.6,
          ease: theme.easeValues.inOut,
        },
      }}
    >
      <motion.div
        animate={{
          rotateY: isReversed ? '180deg' : '0deg',
          transformStyle: 'preserve-3d',
          transition: {
            type: 'tween',
            duration: 0.6,
            ease: theme.easeValues.inOut,
          },
        }}
      >
        <motion.div
          animate={{
            backfaceVisibility: 'hidden',
            transformStyle: 'preserve-3d',
          }}
        >
          <Inner
            {...props}
            isReversed={isReversed && !isSmall}
            isSmall={isSmall}
          />
        </motion.div>
        <Reverse>
          <Inner {...props} isBackside />
        </Reverse>
      </motion.div>
    </StyledItem>
  )
}
